import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import adminProgramService from '@/services/adminProgramService'

const slugify = (value) => {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
  return slug || `program-${Date.now()}`
}

const normalizeList = (value) => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.data)) return value.data
  return []
}

const unwrapData = (response) => response?.data?.data ?? response?.data ?? response

const normalizeSession = (value) => ({
  id: value?.id,
  name: value?.name ?? value?.title ?? '',
  description: value?.description ?? '',
  milestone: value?.milestone ?? '',
  recommended_duration: value?.recommended_duration ?? value?.duration ?? null,
  order: value?.order ?? 0,
  task_title: value?.task_title ?? '',
  task_instructions: value?.task_instructions ?? '',
  task_input_type: value?.task_input_type ?? 'text',
})

const toSessionPayload = (payload) => ({
  name: (payload.name || payload.description?.slice(0, 100) || 'Session').trim(),
  description: payload.description?.trim() ?? '',
  milestone: payload.milestone?.trim() ?? '',
  recommended_duration:
    payload.recommended_duration !== null && payload.recommended_duration !== undefined
      ? Number(payload.recommended_duration)
      : undefined,
  task_title: payload.task_title ?? '',
  task_instructions: payload.task_instructions ?? '',
  task_input_type: payload.task_input_type ?? 'text',
})

export const useAdminProgramCreationStore = defineStore('adminProgramCreation', () => {
  const programId = ref(null)
  const programStatus = ref('draft')
  const isEditing = ref(false)
  const isArchivedProgram = computed(() => programStatus.value === 'archived')

  const programName = ref('')
  const goal = ref('')
  const description = ref('')
  const coverImageUrl = ref('')
  const feedbackMode = ref('')
  const sectors = ref([])
  const skills = ref([])

  // Par defaut : les deux methodes, comme le backend et l'application.
  const pairingMethod = ref('both')
  const introductionSessionRequired = ref(true)

  const sessions = ref([])
  const resources = ref([])
  const questions = ref([])

  const isSubmitting = ref(false)
  const isLoadingProgram = ref(false)
  const isLoadingSessions = ref(false)
  const isLoadingResources = ref(false)
  const isLoadingQuestions = ref(false)
  const error = ref(null)

  const ensureProgramId = () => {
    if (programId.value == null) {
      throw new Error('Program ID is missing. Complete step 1 first.')
    }
    return programId.value
  }

  const assertProgramEditable = () => {
    if (!isArchivedProgram.value) return
    error.value = 'Archived programs are read-only.'
    throw new Error(error.value)
  }

  const resetProgram = () => {
    programId.value = null
    programStatus.value = 'draft'
    isEditing.value = false
    programName.value = ''
    goal.value = ''
    description.value = ''
    coverImageUrl.value = ''
    feedbackMode.value = ''
    sectors.value = []
    skills.value = []
    pairingMethod.value = 'both'
    introductionSessionRequired.value = true
    sessions.value = []
    resources.value = []
    questions.value = []
    error.value = null
  }

  const applyProgramDetail = (detail) => {
    programName.value = detail?.title ?? detail?.name ?? ''
    if (['draft', 'active', 'archived', 'ready'].includes(detail?.status)) {
      programStatus.value = detail.status
    }
    description.value = detail?.description ?? ''
    coverImageUrl.value = detail?.cover_image_url ?? ''
    feedbackMode.value = detail?.feedback_mode ?? ''
    goal.value = detail?.objective ?? ''
    sectors.value = Array.isArray(detail?.sectors) ? detail.sectors : []
    skills.value = Array.isArray(detail?.skills) ? detail.skills : []
    pairingMethod.value = detail?.pairing_method ?? pairingMethod.value
    introductionSessionRequired.value =
      detail?.introduction_session_required ?? introductionSessionRequired.value
  }

  const buildStep1Payload = () => {
    // objective = libelle/slug de GoalCategory (UI "Goal").
    // Le backend derive programs.goal_id (Goal catalogue) via ProgramCatalogGoalService.
    const payload = {
      name: slugify(programName.value),
      title: programName.value.trim(),
      objective: goal.value.trim(),
      description: description.value.trim() || undefined,
      cover_image_url: coverImageUrl.value.trim() || null,
      feedback_mode: feedbackMode.value || null,
      estimated_duration: 12,
      visibility: 'private',
      type: 'standard',
      matching_type: 'manual',
    }
    if (sectors.value.length) payload.sectors = sectors.value
    if (skills.value.length) payload.skills = skills.value
    return payload
  }

  async function loadProgram(id) {
    isLoadingProgram.value = true
    error.value = null
    isEditing.value = true
    programId.value = id
    try {
      const res = await adminProgramService.getOne(id)
      const detail = unwrapData(res)
      applyProgramDetail(detail)
      await Promise.all([fetchSessions(), fetchResources(), fetchQuestions()])
      return detail
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load program'
      throw err
    } finally {
      isLoadingProgram.value = false
    }
  }

  async function submitStep1() {
    if (!programName.value.trim()) {
      error.value = 'Program name is required.'
      throw new Error(error.value)
    }
    if (!goal.value.trim()) {
      error.value = 'Goal is required.'
      throw new Error(error.value)
    }

    assertProgramEditable()
    isSubmitting.value = true
    error.value = null
    try {
      const payload = buildStep1Payload()
      if (isEditing.value && programId.value) {
        const res = await adminProgramService.update(programId.value, payload)
        applyProgramDetail(unwrapData(res))
        return unwrapData(res)
      }
      const res = await adminProgramService.create(payload)
      const created = unwrapData(res)
      programId.value = created?.id ?? null
      if (programId.value) isEditing.value = true
      programStatus.value = created?.status ?? 'draft'
      return created
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to save program'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function submitMatchingType() {
    assertProgramEditable()
    isSubmitting.value = true
    error.value = null
    try {
      const id = ensureProgramId()
      const res = await adminProgramService.update(id, {
        pairing_method: pairingMethod.value,
        introduction_session_required: introductionSessionRequired.value,
      })
      applyProgramDetail(unwrapData(res))
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to save settings'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function fetchSessions() {
    isLoadingSessions.value = true
    try {
      const id = ensureProgramId()
      const res = await adminProgramService.getSessions(id)
      sessions.value = normalizeList(unwrapData(res)).map(normalizeSession)
    } finally {
      isLoadingSessions.value = false
    }
  }

  async function createSession(payload) {
    assertProgramEditable()
    isSubmitting.value = true
    try {
      const id = ensureProgramId()
      const res = await adminProgramService.createSession(id, toSessionPayload(payload))
      const created = normalizeSession(unwrapData(res))
      sessions.value = [...sessions.value, created].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      return created
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create session'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function updateSession(sessionId, payload) {
    assertProgramEditable()
    isSubmitting.value = true
    try {
      const id = ensureProgramId()
      const res = await adminProgramService.updateSession(id, sessionId, toSessionPayload(payload))
      const updated = normalizeSession(unwrapData(res))
      const idx = sessions.value.findIndex((s) => s.id === sessionId)
      if (idx >= 0) sessions.value[idx] = updated
      return updated
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to update session'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function deleteSession(sessionId) {
    assertProgramEditable()
    isSubmitting.value = true
    try {
      const id = ensureProgramId()
      await adminProgramService.deleteSession(id, sessionId)
      sessions.value = sessions.value.filter((s) => s.id !== sessionId)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete session'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function createSessionFromTemplate(templateId) {
    assertProgramEditable()
    isSubmitting.value = true
    try {
      const id = ensureProgramId()
      const res = await adminProgramService.createSessionFromTemplate(id, { template_id: templateId })
      const created = normalizeSession(unwrapData(res))
      sessions.value = [...sessions.value, created].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      return created
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to import session template'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function fetchResources() {
    isLoadingResources.value = true
    try {
      const id = ensureProgramId()
      const res = await adminProgramService.getResources(id)
      resources.value = normalizeList(unwrapData(res))
    } finally {
      isLoadingResources.value = false
    }
  }

  async function addResources(resourceIds) {
    assertProgramEditable()
    isSubmitting.value = true
    try {
      const id = ensureProgramId()
      await adminProgramService.addResources(id, resourceIds)
      await fetchResources()
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to add resources'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function removeResource(resourceId) {
    assertProgramEditable()
    isSubmitting.value = true
    try {
      const id = ensureProgramId()
      await adminProgramService.removeResource(id, resourceId)
      resources.value = resources.value.filter((r) => (r.resource_id ?? r.id) !== resourceId)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to remove resource'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function fetchQuestions() {
    isLoadingQuestions.value = true
    try {
      const id = ensureProgramId()
      const res = await adminProgramService.getQuestions(id)
      questions.value = normalizeList(unwrapData(res))
    } finally {
      isLoadingQuestions.value = false
    }
  }

  async function createQuestion(payload) {
    assertProgramEditable()
    isSubmitting.value = true
    try {
      const id = ensureProgramId()
      const res = await adminProgramService.createQuestion(id, payload)
      const created = unwrapData(res)
      questions.value = [...questions.value, created]
      return created
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to create question'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function deleteQuestion(questionId) {
    assertProgramEditable()
    isSubmitting.value = true
    try {
      const id = ensureProgramId()
      await adminProgramService.deleteQuestion(id, questionId)
      questions.value = questions.value.filter((q) => q.id !== questionId)
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to delete question'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  async function publishProgram() {
    assertProgramEditable()
    isSubmitting.value = true
    try {
      const id = ensureProgramId()
      if (programStatus.value !== 'active') {
        const res = await adminProgramService.publish(id)
        const published = unwrapData(res)
        applyProgramDetail(published)
      }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to publish program'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    programId,
    programStatus,
    isEditing,
    isArchivedProgram,
    programName,
    goal,
    description,
    coverImageUrl,
    feedbackMode,
    sectors,
    skills,
    pairingMethod,
    introductionSessionRequired,
    sessions,
    resources,
    questions,
    isSubmitting,
    isLoadingProgram,
    isLoadingSessions,
    isLoadingResources,
    isLoadingQuestions,
    error,
    resetProgram,
    loadProgram,
    submitStep1,
    submitMatchingType,
    fetchSessions,
    createSession,
    updateSession,
    deleteSession,
    createSessionFromTemplate,
    fetchResources,
    addResources,
    removeResource,
    fetchQuestions,
    createQuestion,
    deleteQuestion,
    publishProgram,
  }
})
